import { addClassNamesToElement, isHTMLElement, removeClassNamesFromElement } from '@lexical/utils';
import {
  $applyNodeReplacement,
  $createTextNode,
  $isElementNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  EditorThemeClasses,
  ElementNode,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedElementNode,
  Spread,
} from 'lexical';

import { mergeNextSiblingListIfSameType, updateChildrenListItemValue } from './formatList';
import { $getListDepth, $wrapInListItem } from './utils';

import { $createListItemNode, $isListItemNode, PhxListItemNode } from './PhxListItemNode';
import { getListMarker, listStyles } from './listStyles';

import invariant from '../../utils/invariant';
import normalizeClassNames from '../../utils/normalizeClassNames';
import 'components/ContentEditorShared/styles.css';
import './styles.css';

export type SerializedListNode = Spread<
  {
    listType: ListType;
    start: number;
    tag: ListNodeTagType;
  },
  SerializedElementNode
>;

export type ListType =
  | 'number'
  | 'alpha'
  | 'bullet'
  | 'check'
  | 'legacy_number'
  | 'legacy_alpha'
  | 'plain_ol'
  | 'plain_ul';

export type ListNodeTagType = 'ul' | 'ol';

const getListTagForType = (listType: ListType): ListNodeTagType => {
  switch (listType) {
    case 'number':
    case 'alpha':
    case 'legacy_number':
    case 'legacy_alpha':
    case 'plain_ol':
      return 'ol';
    case 'bullet':
    case 'check':
    case 'plain_ul':
      return 'ul';
    default:
      return 'ul';
  }
};

/** @noInheritDoc */
export class PhxListNode extends ElementNode {
  /** @internal */
  __tag: ListNodeTagType;
  /** @internal */
  __start: number;
  /** @internal */
  __listType: ListType;
  /** @internal */
  __originalStyle: string | null;
  /** @internal */
  __originalTypeAttr: string | null;

  static getType(): string {
    return 'list';
  }

  static clone(node: PhxListNode): PhxListNode {
    const listType = node.__listType || TAG_TO_LIST_TYPE[node.__tag];
    const newNode = new PhxListNode(listType, node.__start, node.__key);
    newNode.__originalStyle = node.__originalStyle;
    newNode.__originalTypeAttr = node.__originalTypeAttr;
    return newNode;
  }

  constructor(listType: ListType, start: number, key?: NodeKey) {
    super(key);
    const _listType = TAG_TO_LIST_TYPE[listType] || listType;
    this.__listType = _listType;
    this.__tag = getListTagForType(_listType);
    this.__start = start;
    this.__originalStyle = null;
    this.__originalTypeAttr = null;
  }

  getTag(): ListNodeTagType {
    return this.__tag;
  }

  setListType(type: ListType): void {
    const writable = this.getWritable();
    writable.__listType = type;
    writable.__tag = getListTagForType(type);
  }

  getListType(): ListType {
    return this.__listType;
  }

  getStart(): number {
    return this.__start;
  }

  // View

  createDOM(config: EditorConfig, _editor?: LexicalEditor): HTMLElement {
    const tag = this.__tag;
    const dom = document.createElement(tag);

    if (this.__start !== 1) {
      dom.setAttribute('start', String(this.__start));
    }
    // @ts-expect-error Internal field.
    dom.__lexicalListType = this.__listType;

    if (this.__listType === 'plain_ol' || this.__listType === 'plain_ul') {
      if (this.__listType === 'plain_ol') {
        if (this.__originalTypeAttr) {
          dom.setAttribute('type', this.__originalTypeAttr);
        } else {
          dom.style.listStyleType = 'decimal';
        }
      } else if (this.__listType === 'plain_ul') {
        if (!this.__originalTypeAttr) {
          dom.style.listStyleType = 'disc';
        }
      }
      // Set original style last so it doesn't get overwritten
      if (this.__originalStyle) {
        dom.setAttribute('style', this.__originalStyle);
      }
    } else {
      $setListThemeClassNames(dom, config.theme, this);
    }

    return dom;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    if (prevNode.__tag !== this.__tag) {
      return true;
    }

    $setListThemeClassNames(dom, config.theme, this);

    return false;
  }

  static transform(): (node: LexicalNode) => void {
    return (node: LexicalNode) => {
      invariant($isListNode(node), 'node is not a ListNode');
      mergeNextSiblingListIfSameType(node);
      updateChildrenListItemValue(node);
    };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      ol: (node: HTMLElement) => {
        return {
          conversion: $convertListNode,
          priority: 0,
        };
      },
      ul: (node: HTMLElement) => {
        return {
          conversion: $convertListNode,
          priority: 0,
        };
      },
    };
  }

  static importJSON(serializedNode: SerializedListNode): PhxListNode {
    const node = $createListNode(serializedNode.listType, serializedNode.start);
    node.setFormat(serializedNode.format);
    node.setIndent(serializedNode.indent);
    node.setDirection(serializedNode.direction);
    return node;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const { element } = super.exportDOM(editor);
    if (element && isHTMLElement(element)) {
      if (this.__listType !== 'plain_ol' && this.__listType !== 'plain_ul') {
        element.classList.add(`phx-${this.__listType}-listType`);
        if (this.__start !== 1) {
          element.setAttribute('start', String(this.__start));
        }
        if (this.__listType === 'check') {
          element.setAttribute('__lexicalListType', 'check');
        }
        element.setAttribute('data-list-type', this.__listType);
        const level = $getListDepth(this) - 1;
        const marker = getListMarker(this.__listType, level);
        element.style.listStyleType = marker;
      } else {
        // For plain lists, preserve the original style and type attributes
        if (this.__start !== 1) {
          element.setAttribute('start', String(this.__start));
        }
        if (this.__listType === 'plain_ol') {
          if (this.__originalTypeAttr) {
            element.setAttribute('type', this.__originalTypeAttr);
          } else {
            element.style.listStyleType = 'decimal';
          }
        } else if (this.__listType === 'plain_ul') {
          if (!this.__originalTypeAttr) {
            element.style.listStyleType = 'disc';
          }
        }
        // Set original style last so it doesn't get overwritten
        if (this.__originalStyle) {
          element.setAttribute('style', this.__originalStyle);
        }
      }
    }
    return {
      element,
    };
  }

  exportJSON(): SerializedListNode {
    return {
      ...super.exportJSON(),
      listType: this.getListType(),
      start: this.getStart(),
      tag: this.getTag(),
      type: 'list',
      version: 1,
    };
  }

  canBeEmpty(): false {
    return false;
  }

  canIndent(): false {
    return false;
  }

  append(...nodesToAppend: LexicalNode[]): this {
    for (let i = 0; i < nodesToAppend.length; i++) {
      const currentNode = nodesToAppend[i];

      if ($isListItemNode(currentNode)) {
        super.append(currentNode);
      } else {
        const listItemNode = $createListItemNode();

        if ($isListNode(currentNode)) {
          listItemNode.append(currentNode);
        } else if ($isElementNode(currentNode)) {
          const textNode = $createTextNode(currentNode.getTextContent());
          listItemNode.append(textNode);
        } else {
          listItemNode.append(currentNode);
        }
        super.append(listItemNode);
      }
    }
    return this;
  }

  extractWithChild(child: LexicalNode): boolean {
    return $isListItemNode(child);
  }
}

function $setListThemeClassNames(dom: HTMLElement, editorThemeClasses: EditorThemeClasses, node: PhxListNode): void {
  // Skip all operations for plain list types
  if (node.__listType === 'plain_ol' || node.__listType === 'plain_ul') {
    return;
  }

  const classesToAdd = [];
  const classesToRemove = [];
  const listTheme = editorThemeClasses.list;
  classesToAdd.push(`phx-${node.__listType}-listType`);

  const updateListClassNames = (element: HTMLElement, level: number, listType: any) => {
    if (element.tagName.toLowerCase() === 'ol' || element.tagName.toLowerCase() === 'ul') {
      const classesToRemove = Object.values(listStyles).flat();

      if (listType) {
        const currentClass = getListMarker(listType, level);
        removeClassNamesFromElement(element, ...classesToRemove);
        addClassNamesToElement(element, currentClass);
      }
    }
  };
  const listDepth = $getListDepth(node) - 1;

  if (listTheme !== undefined) {
    const listDepth = $getListDepth(node) - 1;
    const listClassName = listTheme[node.__tag];
    let nestedListClassName;
    const nestedListTheme = listTheme.nested;
    const checklistClassName = listTheme.checklist;
    if (nestedListTheme !== undefined && nestedListTheme.list) {
      nestedListClassName = nestedListTheme.list;
    }

    if (listClassName !== undefined) {
      classesToAdd.push(listClassName);
    }

    if (checklistClassName !== undefined && node.__listType === 'check') {
      classesToAdd.push(checklistClassName);
    }

    if (nestedListClassName !== undefined) {
      const nestedListItemClasses = normalizeClassNames(nestedListClassName);

      if (listDepth > 1) {
        classesToAdd.push(...nestedListItemClasses);
      } else {
        classesToRemove.push(...nestedListItemClasses);
      }
    }
  }

  if (classesToRemove.length > 0) {
    removeClassNamesFromElement(dom, ...classesToRemove);
  }

  if (classesToAdd.length > 0) {
    addClassNamesToElement(dom, ...classesToAdd);
  }

  // Apply list-specific styling based on type and depth
  updateListClassNames(dom, listDepth, node.__listType);
}

/*
 * This function normalizes the children of a ListNode after the conversion from HTML,
 * ensuring that they are all ListItemNodes and contain either a single nested ListNode
 * or some other inline content.
 */
function $normalizeChildren(nodes: Array<LexicalNode>): Array<PhxListItemNode> {
  const normalizedListItems: Array<PhxListItemNode> = [];
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if ($isListItemNode(node)) {
      normalizedListItems.push(node);
      const children = node.getChildren();
      if (children.length > 1) {
        children.forEach((child) => {
          if ($isListNode(child)) {
            normalizedListItems.push($wrapInListItem(child));
          }
        });
      }
    } else {
      normalizedListItems.push($wrapInListItem(node));
    }
  }
  return normalizedListItems;
}

function isDomChecklist(domNode: HTMLElement) {
  if (
    domNode.getAttribute('__lexicallisttype') === 'check' ||
    // is github checklist
    domNode.classList.contains('contains-task-list')
  ) {
    return true;
  }
  // if children are checklist items, the node is a checklist ul. Applicable for googledoc checklist pasting.
  for (const child of domNode.childNodes) {
    if (isHTMLElement(child) && child.hasAttribute('aria-checked')) {
      return true;
    }
  }
  return false;
}

function $convertListNode(domNode: HTMLElement): DOMConversionOutput {
  const nodeName = domNode.nodeName.toLowerCase();
  let node = null;
  const listType: ListType | undefined = domNode.dataset.listType as ListType | undefined;

  if (listType) {
    const start = (domNode as HTMLOListElement).start || 1;
    node = $createListNode(listType, start);
  } else {
    // If no data-list-type is present, create a plain list
    const start = (domNode as HTMLOListElement).start || 1;
    node = $createListNode(nodeName === 'ol' ? 'plain_ol' : 'plain_ul', start);
    // Store the original style and type for plain lists
    node.__originalStyle = domNode.getAttribute('style');
    if (nodeName === 'ol') {
      node.__originalTypeAttr = domNode.getAttribute('type');
    }
  }

  return {
    after: $normalizeChildren,
    node,
  };
}

const TAG_TO_LIST_TYPE: Record<string, ListType> = {
  ol: 'number',
  ul: 'bullet',
};

/**
 * Creates a ListNode of listType.
 * @param listType - The type of list to be created. Can be 'number', 'alpha', 'bullet', or 'check'.
 * @param start - Where an ordered list starts its count, start = 1 if left undefined.
 * @returns The new ListNode
 */
export function $createListNode(listType: ListType, start = 1): PhxListNode {
  return $applyNodeReplacement(new PhxListNode(listType, start));
}

/**
 * Checks to see if the node is a ListNode.
 * @param node - The node to be checked.
 * @returns true if the node is a ListNode, false otherwise.
 */
export function $isListNode(node: LexicalNode | null | undefined): node is PhxListNode {
  return node instanceof PhxListNode;
}
